import { usePathname } from '@/routes/hooks';
import { useBoolean } from 'minimal-shared/hooks';
import { isEqualPath, isActiveLink, isExternalLink } from 'minimal-shared/utils';
import { useRef, useEffect, useCallback } from 'react';

import type { NavListProps } from '../types';

import { Nav, NavLi, NavUl, NavDropdown } from '../components';
import { NavItem } from './nav-desktop-item';

// ----------------------------------------------------------------------

export function NavList({ data, sx, ...other }: NavListProps) {
  const pathname = usePathname();
  const navItemRef = useRef<HTMLButtonElement>(null);

  const isActive = isActiveLink(pathname, data.path, !!data.children);
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.children) {
      onOpen();
    }
  }, [data.children, onOpen]);

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      title={data.title}
      // state
      open={open}
      active={isActive}
      // options
      hasChild={!!data.children}
      externalLink={isExternalLink(data.path)}
      // action
      onMouseEnter={handleOpenMenu}
      onMouseLeave={onClose}
    />
  );

  const renderDropdown = () =>
    !!data.children && (
      <NavDropdown open={open} onMouseEnter={handleOpenMenu} onMouseLeave={onClose}>
        <Nav>
          <NavUl sx={{ flexDirection: 'column', mr: 0.75 }}>
            {data.children.map((item) => (
              <NavLi key={item.title} sx={{ mt: 0.75 }}>
                <NavItem
                  subItem
                  title={item.title}
                  path={item.path}
                  active={isEqualPath(item.path, pathname)}
                />
              </NavLi>
            ))}
          </NavUl>
        </Nav>
      </NavDropdown>
    );

  return (
    <NavLi sx={sx} {...other}>
      {renderNavItem()}
      {renderDropdown()}
    </NavLi>
  );
}
